import {
    CHUNK_SIZE,
    JUMPSQUAT_DURATION,
    MENTOR_PLATFORM_X,
    MIN_JUMP_TILES,
    MOVE_INTERVAL,
    PLAYER_HEIGHT_CROUCHING,
    PLAYER_HEIGHT_STANDING,
    PlayerState,
    SLASH_RATE,
    SWORD_DESTROY_COOLDOWN_TICKS,
    TICK_INTERVAL,
    TileType,
    isBodyPassable,
    isInSpawnProtection,
    isLadder,
    isPassable,
} from '@blockworld/shared';
import { handleDownAction, executeDownAction, handleAttackBlockAction, executeAttackBlockAction } from './WorldInteractionSystem';

export interface GameSceneMotionContext {
    [key: string]: any;
}

/**
 * Extracted from GameScene movement pipeline as a move-only refactor.
 */
export function updateLocalPlayer(scene: GameSceneMotionContext, now: number): void {
    if (!scene.localPlayer) return;
    const player = scene.localPlayer;

    // Hurt stun: skip all input/movement for 2 ticks after taking damage.
    // Input is naturally buffered — keys held during stun fire on the first tick after.
    const hurtTick = Math.floor(now / MOVE_INTERVAL);
    if (hurtTick < scene.hurtUntilTick) {
        return;
    }

    processBlockInput(scene, player);
    processCombatInput(scene, player, now);

    // Movement and jump/fall handle the player's own state machine.
    // Their return value gates further player-movement processing below,
    // but must NOT prevent the global tick from running — world simulation
    // (falling blocks, server updates, other-player prediction) must always progress.
    const movementHandled = processMovementInput(scene, player, now);
    const jumpFallHandled = !movementHandled && processJumpAndFall(scene, player, now);
    const playerHandled = movementHandled || jumpFallHandled;

    const globalTick = Math.floor(now / MOVE_INTERVAL);
    const isTickReady = globalTick > scene.lastGlobalFallTick;
    const wasFalling = player.state === PlayerState.FALLING;
    const wantsHorizontal = (scene.inputState.left && !scene.inputState.right) ||
                           (scene.inputState.right && !scene.inputState.left);

    // Global tick ALWAYS runs — falling blocks, server block updates, other-player
    // fall prediction, and sword tile checks must tick even during ladder movement.
    processGlobalTick(scene, player, now, globalTick, isTickReady);

    if (playerHandled) {
        return;
    }

    // Sanity check every frame: if FALLING but on ground, correct state
    // SKIP if pendingLedgeDir is set - we're intentionally about to walk off
    if (player.state === PlayerState.FALLING && scene.isOnGround(player) && player.pendingLedgeDir === 0) {
        player.state = player.isCrouching ? PlayerState.CROUCHING : PlayerState.IDLE;
        // Fresh 100ms lockout from landing — prevents stale lastUnifiedMoveTime
        // (e.g. from RISING phase) from allowing an immediate move after landing
        scene.lastUnifiedMoveTime = now;
    }

    // Safeguard: if RISING but stuck (no Y progress for 300ms), force to FALLING
    // This prevents rare edge cases where player gets stuck mid-air
    if (player.state === PlayerState.RISING) {
        if (player.y < player.lastRisingY) {
            // Made upward progress - reset timer
            player.lastRisingY = player.y;
            player.risingStartTime = now;
        } else if (now - player.risingStartTime > 300) {
            // Stuck for too long - force falling
            console.warn('RISING stuck safeguard triggered', {
                playerY: player.y,
                lastRisingY: player.lastRisingY,
                jumpRemaining: player.jumpRemaining,
                minJumpRemaining: player.minJumpRemaining,
                upHeld: scene.inputState.up,
                lastUnifiedMoveTime: scene.lastUnifiedMoveTime,
                timeSinceLastMove: now - scene.lastUnifiedMoveTime,
                isOnGround: scene.isOnGround(player),
                tileAboveHead: scene.getTile(player.x, player.y - 1),
                pendingLedgeDir: player.pendingLedgeDir,
                horizontalJustPressed: scene.horizontalJustPressed,
                wantsHorizontal: (scene.inputState.left && !scene.inputState.right) || (scene.inputState.right && !scene.inputState.left),
            });
            player.state = PlayerState.FALLING;
            player.minJumpRemaining = 0;
        }
    }

    // If we were falling this frame (even if we landed), skip other movement
    // This prevents double-moves and state thrashing
    // EXCEPTION: allow instant first horizontal move while falling (but not if pending ledge)
    if (wasFalling && (!scene.horizontalJustPressed || player.pendingLedgeDir !== 0)) {
        return;
    }

    // INSTANT FIRST MOVE - horizontal just pressed
    // Rate limited to prevent tap-tap-tap spam, but resets when blocked (standstill)
    // Skip if pendingLedgeDir is set - we're waiting for global tick
    if (player.pendingLedgeDir !== 0) {
        return;
    }

    if (scene.horizontalJustPressed && wantsHorizontal &&
        now - scene.lastHorizontalMoveTime >= MOVE_INTERVAL) {
        const dir = scene.inputState.left ? -1 : 1;
        player.facing = dir;

        // Check for walk-off-ledge BEFORE trying to move
        // Walk-off-ledge queues move for global tick to sync with fall
        if (scene.isWalkOffLedge(player, dir)) {
            player.pendingLedgeDir = dir;
            player.state = PlayerState.FALLING;
            scene.lastHorizontalMoveTime = now;
            scene.lastUnifiedMoveTime = now;
            return;
        }

        // Track if horizontal move causes step-up (vertical change)
        const xBefore = player.x;
        const yBefore = player.y;

        // Only update timing if we actually moved (not blocked)
        if (scene.tryMoveHorizontal(player, dir)) {
            scene.lastHorizontalMoveTime = now;
            // Send air strafe if falling or rising (airborne)
            if (player.state === PlayerState.FALLING || player.state === PlayerState.RISING) {
                scene.networkClient.sendAirStrafe(dir);
                // Skip next global tick's horizontal - this instant move counts as that tick's move
                scene.skipNextTickHorizontal = true;
            }
        }

        const didStepUp = player.y < yBefore;

        // If RISING AND tick was ready AND didn't step up, do vertical move
        // Step-up already consumed this tick's vertical movement
        const tickWasReady = now - scene.lastUnifiedMoveTime >= MOVE_INTERVAL;
        let didVerticalMove = false;
        if (player.state === PlayerState.RISING && tickWasReady && !didStepUp) {
            doVerticalMove(scene, player, now);
            didVerticalMove = true;
        }

        // If RISING with step-up, consume jump (diagonal movement)
        if (player.state === PlayerState.RISING && didStepUp) {
            player.jumpRemaining--;
            if (player.minJumpRemaining > 0) player.minJumpRemaining--;
            // Ladder jump refresh: entered ladder diagonally via step-up
            if (scene.isInLadder(player)) {
                player.jumpRemaining = player.jumpHeight;
            }
        }

        // If FALLING and now on ground (step-up landed), update state
        if (player.state === PlayerState.FALLING && scene.isOnGround(player)) {
            player.state = player.isCrouching ? PlayerState.CROUCHING : PlayerState.IDLE;
        }

        // Only reset unified tick timer if we actually did movement
        // Otherwise spamming blocked horizontal during RISING would prevent vertical moves
        const didAnyMovement = (player.x !== xBefore) || (player.y !== yBefore) || didVerticalMove;
        if (didAnyMovement) {
            scene.lastUnifiedMoveTime = now;
        }

        // Check if we walked off a ledge (will set state but not move)
        checkFallOffLedge(scene, player);
        return;
    }

    // UNIFIED TICK - for held/continuous movement (when NOT falling)
    if (player.state === PlayerState.FALLING) return;

    const tickReady = now - scene.lastUnifiedMoveTime >= MOVE_INTERVAL;
    if (!tickReady) return;

    scene.lastUnifiedMoveTime = now;

    // Try horizontal FIRST - step-up takes priority over rise
    // This prevents +2 vertical in one tick (rise + step-up)
    let didStepUp = false;
    if (wantsHorizontal) {
        const dir = scene.inputState.left ? -1 : 1;
        player.facing = dir;

        // Check for walk-off-ledge - queue for global tick
        if (scene.isWalkOffLedge(player, dir)) {
            player.pendingLedgeDir = dir;
            player.state = PlayerState.FALLING;
            scene.lastHorizontalMoveTime = now;
            return; // Skip rest - will process on global tick
        }

        const xBefore = player.x;
        const yBefore = player.y;
        if (scene.tryMoveHorizontal(player, dir)) {
            scene.lastHorizontalMoveTime = now;
            didStepUp = player.y < yBefore;
            if (player.x !== xBefore) {
                console.log(`[PLAYER MOVE] oldX=${xBefore}, newX=${player.x}, now=${now}, globalTick=${Math.floor(now / MOVE_INTERVAL)}, MOVE_INTERVAL=${MOVE_INTERVAL}`);
            }
        }
    }

    // Process pending slash AFTER horizontal movement
    // This ensures slash spawns at player's new position, synced with walk tick
    if (player.pendingSlash) {
        player.pendingSlash = false;
        scene.spawnLocalSlash(player, now);
        player.lastSlashSpawnTick = Math.floor(now / MOVE_INTERVAL);
    }

    // Vertical movement - skip if we already stepped up
    if (!didStepUp) {
        doVerticalMove(scene, player, now);
    } else if (player.state === PlayerState.RISING) {
        // Step-up during RISING is diagonal movement - consume jump and keep rising
        player.jumpRemaining--;
        if (player.minJumpRemaining > 0) player.minJumpRemaining--;
        // Ladder jump refresh: entered ladder diagonally via step-up
        if (scene.isInLadder(player)) {
            player.jumpRemaining = player.jumpHeight;
        }
        // State stays RISING - next tick doVerticalMove will check if arc is done
    }

    // Check if we walked off a ledge
    checkFallOffLedge(scene, player);


    processContinuousCombatInput(scene, player, now);
}

export function processBlockInput(scene: GameSceneMotionContext, player: any): void {
    // Down action (instant)
    if (scene.inputState.downJustPressed) {
        handleDownAction(scene, player);
    }

    // Spacebar/A-button block interaction: pickup adjacent blocks or place above head
    // Takes priority over sword — if a block action occurs, sword is suppressed
    scene.attackConsumedByBlock = false;
    if (scene.inputState.attackJustPressed) {
        if (handleAttackBlockAction(scene, player)) {
            scene.attackConsumedByBlock = true;
        }
    }
}

export function processCombatInput(scene: GameSceneMotionContext, player: any, now: number): void {
    // Sword/club hold (attack button) — requires IRON_SWORD or ILO_IJO equipped
    // Skip sword initiation if spacebar was used for a block interaction this frame
    const isMeleeItem = player.equippedItem === TileType.IRON_SWORD || player.equippedItem === TileType.ILO_IJO;
    if (scene.inputState.attackJustPressed && !scene.attackConsumedByBlock && !player.isHoldingSword && isMeleeItem) {
        // No sword in spawn protection zone
        if (isInSpawnProtection(player.x, player.y, scene.roomBoxes)) {
            // If within 1 tile of mentor, turn mentor's attention to the player
            if (Math.abs(player.x - MENTOR_PLATFORM_X) <= 1 && scene.mentorBehavior) {
                const offset = scene.getCameraOffset();
                scene.mentorBehavior.notifyPlayerAttention(
                    player.x, offset.offsetX, offset.offsetY, now,
                );
            }
            return;
        }

        player.isHoldingSword = true;
        player.swordHoldStartTick = Math.floor(now / TICK_INTERVAL);
        scene.networkClient.sendSwordHold(true);

        // Club (ILO_IJO): melee only, no slash spawning
        if (player.equippedItem === TileType.ILO_IJO) {
            scene.lastHorizontalMoveTime = now;
            return;
        }

        // NOTE: Do NOT call predictSwordTile() here. The per-tick continuous
        // check (below, in the globalTickChanged block) handles it once per tick,
        // matching the server's tickCombat cadence. Calling it here AND on the
        // tick boundary causes a double-conversion (e.g. LADDER→STICK→SKY in one
        // tick) that races ahead of the server's single-step-per-tick processing.

        // Check cooldown - prevent mashing from bypassing SLASH_RATE
        const currentTick = Math.floor(now / TICK_INTERVAL);
        const ticksSinceLastSlash = currentTick - player.lastSlashSpawnTick;
        const canSpawnSlash = ticksSinceLastSlash >= SLASH_RATE;

        if (canSpawnSlash) {
            // Check if player is holding a direction (will move)
            // If so, queue slash to spawn AFTER movement for correct positioning
            const wantsHorizontal = (scene.inputState.left && !scene.inputState.right) ||
                                   (scene.inputState.right && !scene.inputState.left);

            if (wantsHorizontal) {
                // Queue slash for next tick - spawn AFTER player moves
                player.pendingSlash = true;
            } else {
                // Not moving - spawn immediately
                scene.spawnLocalSlash(player, now);
                player.lastSlashSpawnTick = currentTick;
            }
        }

        // Consume instant horizontal timing - sword swing takes the "instant" slot
        // Any direction press right after must wait for next tick
        scene.lastHorizontalMoveTime = now;
    } else if (!scene.inputState.attack && player.isHoldingSword) {
        player.isHoldingSword = false;
        scene.networkClient.sendSwordHold(false);
    }
}

export function processMovementInput(scene: GameSceneMotionContext, player: any, now: number): boolean {
    // Uncrouch (instant)
    if (!scene.inputState.down && player.isCrouching) {
        scene.tryEndCrouch(player);
    }

    // Ladder climbing (grounded states only — RISING/FALLING pass through normally)
    const inGroundedState = player.state === PlayerState.IDLE ||
                            player.state === PlayerState.WALKING ||
                            player.state === PlayerState.CROUCHING;
    if (inGroundedState && scene.isOnOrInLadder(player)) {
        const tickReady = now - scene.lastUnifiedMoveTime >= MOVE_INTERVAL;
        const ladderWantsHorizontal = (scene.inputState.left && !scene.inputState.right) ||
                                      (scene.inputState.right && !scene.inputState.left);
        // Clear ascending flag when UP is released
        if (!scene.inputState.up && !scene.pendingLadderClimb) {
            scene.wasClimbingLadder = false;
        }
        if ((scene.inputState.up || scene.pendingLadderClimb) && scene.isInLadder(player)) {
            // Buffer horizontal tap for diagonal climbing
            if (scene.horizontalJustPressed && ladderWantsHorizontal) {
                scene.pendingLadderHorizontalDir = scene.inputState.left ? -1 : 1;
            }
            // Climb up — tick-gated, with diagonal horizontal in the same tick
            if (tickReady) {
                scene.pendingLadderClimb = false;
                // Resolve horizontal: buffered tap OR currently held
                const horizDir = scene.pendingLadderHorizontalDir !== 0
                    ? scene.pendingLadderHorizontalDir
                    : (ladderWantsHorizontal ? (scene.inputState.left ? -1 : 1) : 0);
                scene.pendingLadderHorizontalDir = 0;

                const tileAboveHead = scene.getTile(player.x, player.y - 1);
                if (isBodyPassable(tileAboveHead)) {
                    // Check if this climb would exit water — block it at the surface
                    const h_check = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
                    let bodyInWater = false;
                    for (let y = 0; y < h_check; y++) {
                        if (scene.getTile(player.x, player.y + y) === TileType.WATER) {
                            bodyInWater = true;
                            break;
                        }
                    }
                    let destHasLadder = false;
                    if (bodyInWater) {
                        for (let y = 0; y < h_check; y++) {
                            if (isLadder(scene.getTile(player.x, player.y - 1 + y))) {
                                destHasLadder = true;
                                break;
                            }
                        }
                    }
                    if (bodyInWater && !destHasLadder) {
                        // Water surface: allow climb to head-poking-out but no RISING
                        player.y--;
                        scene.lastUnifiedMoveTime = now;
                        scene.lastHorizontalMoveTime = now;
                        player.pendingLedgeDir = 0;
                        // Don't set wasClimbingLadder — prevents RISING on next frame
                        // No diagonal movement — stay at water surface column
                    } else {
                    const wasAlreadyClimbing = scene.wasClimbingLadder;
                    player.y--;
                    scene.lastUnifiedMoveTime = now;
                    scene.lastHorizontalMoveTime = now;
                    player.pendingLedgeDir = 0;
                    scene.wasClimbingLadder = true;

                    // Diagonal climb: also move horizontal
                    // Can't use tryMoveHorizontal — it refuses walk-off-ledge for grounded players,
                    // but we WANT to leave the ladder into sky to start a jump.
                    if (horizDir !== 0) {
                        player.facing = horizDir;
                        const newX = player.x + horizDir;
                        const h = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
                        let canFit = true;
                        for (let y = 0; y < h; y++) {
                            if (!isBodyPassable(scene.getTile(newX, player.y + y))) {
                                canFit = false;
                                break;
                            }
                        }
                        if (canFit) {
                            player.x = newX;
                            scene.lastHorizontalMoveTime = now;
                        }
                    }

                    // If we've left the ladder, transition straight to RISING (no jumpsquat)
                    // Only auto-jump if the player was already climbing (continuous hold) or
                    // moved diagonally off the ladder. A single tap that exits the ladder
                    // should NOT auto-jump — the player just wanted to climb one step.
                    // If jumpHeight is 0, skip RISING and fall instead.
                    if (!scene.isInLadder(player) && (wasAlreadyClimbing || horizDir !== 0)) {
                        scene.wasClimbingLadder = false;
                        if (player.jumpHeight > 0) {
                            player.state = PlayerState.RISING;
                            player.risingStartTime = now;
                            player.lastRisingY = player.y;
                            player.jumpRemaining = player.jumpHeight;
                            player.minJumpRemaining = MIN_JUMP_TILES;
                            player.pendingLedgeDir = 0;
                        } else {
                            player.state = PlayerState.FALLING;
                        }
                    }
                    } // end else (not at water surface)
                } else if (horizDir !== 0) {
                    // Can't climb up (ceiling), but horizontal movement is still valid
                    player.facing = horizDir;
                    const newX = player.x + horizDir;
                    const h_horiz = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
                    let canFit = true;
                    for (let y = 0; y < h_horiz; y++) {
                        if (!isBodyPassable(scene.getTile(newX, player.y + y))) {
                            canFit = false;
                            break;
                        }
                    }
                    if (canFit) {
                        player.x = newX;
                        scene.lastHorizontalMoveTime = now;
                        scene.lastUnifiedMoveTime = now;
                    }
                }
            }
            // Always consume UP on ladder — never fall through to jumpsquat
            return true;
        } else if (scene.inputState.up && scene.wasClimbingLadder) {
            // Seamless jump from ascending — no jumpsquat (body cleared the ladder top)
            // But suppress if we just exited water — can't jump out of water
            const h_wc = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
            const belowFeet = scene.getTile(player.x, player.y + h_wc);
            if (belowFeet === TileType.WATER || belowFeet === TileType.WATER_SURFACE) {
                scene.wasClimbingLadder = false;
                return true;
            }
            scene.wasClimbingLadder = false;
            if (tickReady) {
                if (player.jumpHeight > 0) {
                    player.state = PlayerState.RISING;
                    player.risingStartTime = now;
                    player.lastRisingY = player.y;
                    player.jumpRemaining = player.jumpHeight;
                    player.minJumpRemaining = MIN_JUMP_TILES;
                    player.pendingLedgeDir = 0;
                    if (scene.tryMoveUp(player)) {
                        player.jumpRemaining--;
                        if (player.minJumpRemaining > 0) player.minJumpRemaining--;
                        if (scene.isInLadder(player)) {
                            player.jumpRemaining = player.jumpHeight;
                        }
                    } else {
                        player.minJumpRemaining = 0;
                        player.state = PlayerState.FALLING;
                    }
                } else {
                    player.state = PlayerState.FALLING;
                }
                scene.lastUnifiedMoveTime = now;
            }
            // Consume UP — never fall through to jumpsquat while ascending flag is active
            return true;
        } else if (scene.inputState.down || scene.pendingLadderDescend) {
            scene.wasClimbingLadder = false;
            // Buffer horizontal tap for diagonal descent
            if (scene.horizontalJustPressed && ladderWantsHorizontal) {
                scene.pendingLadderHorizontalDir = scene.inputState.left ? -1 : 1;
            }
            // Climb down — tick-gated, with diagonal horizontal in the same tick
            if (tickReady) {
                scene.pendingLadderDescend = false;
                // Resolve horizontal: buffered tap OR currently held
                const horizDir = scene.pendingLadderHorizontalDir !== 0
                    ? scene.pendingLadderHorizontalDir
                    : (ladderWantsHorizontal ? (scene.inputState.left ? -1 : 1) : 0);
                scene.pendingLadderHorizontalDir = 0;

                const h = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
                const tileBelowFeet = scene.getTile(player.x, player.y + h);
                if (isPassable(tileBelowFeet) || isLadder(tileBelowFeet)) {
                    player.y++;
                    scene.lastUnifiedMoveTime = now;
                    scene.lastHorizontalMoveTime = now;

                    // Diagonal descent: also move horizontal
                    // Can't use tryMoveHorizontal — it refuses walk-off-ledge for grounded players,
                    // but we WANT to leave the ladder into sky to start falling.
                    if (horizDir !== 0) {
                        player.facing = horizDir;
                        const newX = player.x + horizDir;
                        const h = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
                        let canFit = true;
                        for (let y = 0; y < h; y++) {
                            if (!isBodyPassable(scene.getTile(newX, player.y + y))) {
                                canFit = false;
                                break;
                            }
                        }
                        if (canFit) {
                            player.x = newX;
                            scene.lastHorizontalMoveTime = now;
                        }
                    }

                    // If we've left the ladder, transition to FALLING
                    // Consume current global tick so fall waits for the NEXT one
                    if (!scene.isInLadder(player)) {
                        scene.lastGlobalFallTick = Math.floor(now / MOVE_INTERVAL);
                        player.state = PlayerState.FALLING;
                    }
                } else if (horizDir !== 0) {
                    // Can't descend (floor), but horizontal movement is still valid
                    player.facing = horizDir;
                    const newX = player.x + horizDir;
                    let canFit = true;
                    for (let y = 0; y < h; y++) {
                        if (!isBodyPassable(scene.getTile(newX, player.y + y))) {
                            canFit = false;
                            break;
                        }
                    }
                    if (canFit) {
                        player.x = newX;
                        scene.lastHorizontalMoveTime = now;
                        scene.lastUnifiedMoveTime = now;
                    }
                }
            }
            return true;
        }

        // Water sinking: if head is above water and not holding UP, sink 1 tile
        if (!scene.inputState.up && tickReady) {
            const h_sink = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
            const groundTile = scene.getTile(player.x, player.y + h_sink);
            const headTile = scene.getTile(player.x, player.y);
            const headAboveWater = headTile !== TileType.WATER && headTile !== TileType.WATER_SURFACE;
            if (groundTile === TileType.WATER && headAboveWater) {
                // Head poking out, not holding UP — sink to submerge
                if (isBodyPassable(scene.getTile(player.x, player.y + h_sink))) {
                    player.y++;
                    scene.lastUnifiedMoveTime = now;
                    scene.lastHorizontalMoveTime = now;
                }
            }
        }
    } else {
        // Not on/in ladder — clear ladder-specific buffered input.
        // pendingLadderClimb is cleared when UP is not held, to prevent stale
        // flags from a previous jump causing phantom climbs on ladders later.
        // When UP IS held, the flag is preserved for portal activation in
        // processJumpAndFall() (which runs after this function).
        scene.wasClimbingLadder = false;
        scene.pendingLadderDescend = false;
        scene.pendingLadderHorizontalDir = 0;
        if (!scene.inputState.up) {
            scene.pendingLadderClimb = false;
        }
    }

    return false;
}

export function processJumpAndFall(scene: GameSceneMotionContext, player: any, now: number): boolean {
    // Jump initiation (instant)
    // Can't jump while standing on water
    const h_jump = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
    const tileUnderFeet = scene.getTile(player.x, player.y + h_jump);
    const standingOnWater = tileUnderFeet === TileType.WATER || tileUnderFeet === TileType.WATER_SURFACE;
    scene.standingOnRune = tileUnderFeet === TileType.RUNE;
    if (scene.inputState.up && scene.isOnGround(player) &&
        player.state !== PlayerState.JUMPSQUAT &&
        player.state !== PlayerState.RISING &&
        !standingOnWater &&
        !scene.wasClimbingLadder &&
        player.jumpHeight > 0) {
        if (scene.standingOnRune) {
            // Activate portal instead of jumping.
            // Only send once per UP press (pendingLadderClimb is set on up-just-pressed).
            // Sending every frame while held causes concurrent teleportTo() calls that
            // create duplicate server sessions.
            if (scene.pendingLadderClimb) {
                scene.pendingLadderClimb = false;
                scene.networkClient.sendPortalActivate();
            }
        } else {
            scene.pendingLadderClimb = false; // Consume: prevents stale UP from triggering phantom ladder climb later
            player.state = PlayerState.JUMPSQUAT;
            player.jumpsquatStartTime = now;
            player.jumpRemaining = player.jumpHeight;
            player.minJumpRemaining = MIN_JUMP_TILES;
            player.pendingLedgeDir = 0; // Clear any pending ledge - jump overrides it
        }
    }

    // Jumpsquat timing - when complete, immediately do first rise
    if (player.state === PlayerState.JUMPSQUAT) {
        if (now - player.jumpsquatStartTime >= JUMPSQUAT_DURATION) {
            player.state = PlayerState.RISING;
            player.risingStartTime = now;
            player.lastRisingY = player.y;

            // Check if horizontal is buffered during jumpsquat
            const wantsHorizontalNow = (scene.inputState.left && !scene.inputState.right) ||
                                       (scene.inputState.right && !scene.inputState.left);

            // Try horizontal FIRST (step-up counts as vertical)
            let didStepUp = false;
            if (wantsHorizontalNow) {
                const dir = scene.inputState.left ? -1 : 1;
                player.facing = dir;
                const yBefore = player.y;
                if (scene.tryMoveHorizontal(player, dir)) {
                    scene.lastHorizontalMoveTime = now;
                    didStepUp = player.y < yBefore;
                }
            }

            // Do the first rise move (unless step-up already moved us up)
            if (!didStepUp) {
                if (scene.tryMoveUp(player)) {
                    player.jumpRemaining--;
                    if (player.minJumpRemaining > 0) player.minJumpRemaining--;
                    // Ladder jump refresh: seamless, only counter changes
                    if (scene.isInLadder(player)) {
                        player.jumpRemaining = player.jumpHeight;
                    }
                } else {
                    // Bonked head - immediately start falling
                    player.minJumpRemaining = 0;
                    player.state = PlayerState.FALLING;
                }
            } else {
                // Step-up counts as first vertical move - consume jump
                player.jumpRemaining--;
                if (player.minJumpRemaining > 0) player.minJumpRemaining--;
                // Ladder jump refresh: entered ladder diagonally via step-up
                if (scene.isInLadder(player)) {
                    player.jumpRemaining = player.jumpHeight;
                }
            }

            scene.lastUnifiedMoveTime = now; // Subsequent rises follow tick timing
        }
        return true;
    }

    return false;
}

export function processGlobalTick(scene: GameSceneMotionContext, player: any, now: number, globalTick: number, isTickReady: boolean): void {
    // GLOBAL ALIGNED FALLING - check every frame, synced to server block ticks
    // This handles the VERTICAL fall movement only
    const globalTickChanged = isTickReady;

    const wantsHorizontal = (scene.inputState.left && !scene.inputState.right) ||
                           (scene.inputState.right && !scene.inputState.left);


    if (globalTickChanged) {
        scene.lastGlobalFallTick = globalTick;

        // Ground check callback for remote player fall prediction
        const canFall = (x: number, y: number, isCrouching: boolean): boolean => {
            const h = isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
            // Can't fall if tile below feet is solid
            if (!isPassable(scene.getTile(x, y + h))) return false;
            // Can't fall if body overlaps a ladder
            for (let dy = 0; dy < h; dy++) {
                if (isLadder(scene.getTile(x, y + dy))) return false;
            }
            return true;
        };

        // Step all other players on global tick (syncs their falls with blocks)
        for (const queue of scene.otherPlayers.values()) {
            queue.stepOnTick(globalTick, canFall);
        }

        // Process pending server block updates immediately (no buffer delay)
        // Sync comes from processing on same global tick, not from delaying
        for (const update of scene.pendingServerBlockUpdates) {
            const cx = Math.floor(update.x / CHUNK_SIZE);
            const cy = Math.floor(update.y / CHUNK_SIZE);
            const chunkKey = `${cx},${cy}`;
            const chunk = scene.chunks.get(chunkKey);
            const localX = chunk ? ((update.x % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE : 0;
            const localY = chunk ? ((update.y % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE : 0;

            // For local echo place actions, skip the tile write entirely.
            // The client already set the tile at placement time, and the
            // falling block prediction may have moved it since then.
            // Writing it back would create a duplicate block.
            //
            // For pickup/combine, local prediction already set this tile to SKY.
            // If a falling block has since landed here, don't overwrite it
            // with the stale server echo — the server doesn't broadcast
            // falling block movement, so the client prediction is authoritative.
            const currentTile = chunk ? chunk[localY][localX] : TileType.SKY;
            const key = `${update.x},${update.y}`;

            // Detect local echo: either from blockAction (has playerId) or from
            // combat prediction (sword/slash tile destruction tracked in predictedCombatDestroys).
            // Combat events lack playerId, so we match against predictions to avoid
            // overwriting blocks that fell into a position after we predicted destruction.
            const isLocalEchoPlace = update.isLocalEcho && update.action === 'place';
            const isPredictedCombatEcho = !update.isLocalEcho && update.action === 'place' &&
                scene.predictedCombatDestroys.has(key);
            const isLocalPrediction = isLocalEchoPlace || isPredictedCombatEcho;
            if (isPredictedCombatEcho) {
                scene.predictedCombatDestroys.delete(key);
            }

            const fallingBlockLandedHere = update.action !== 'place' && scene.isFallingType(currentTile);

            if (chunk && !fallingBlockLandedHere && !isLocalPrediction) {
                chunk[localY][localX] = update.tileType;
            }

            if (update.action === 'place' && scene.isFallingType(update.tileType)) {
                if (isLocalPrediction) {
                    // Local player's echo — block already predicted in localFallingBlocks
                } else {
                    // Tree-chop or simulation event — 1-tick grace period
                    scene.freshFallingBlocks.add(key);
                }
                // Track LOG inertia: prefer server-provided inertiaDir, fall back to local player's facing
                if (update.tileType === TileType.LOG) {
                    const inertia = update.inertiaDir ?? (scene.localPlayer ? scene.localPlayer.facing : undefined);
                    if (inertia !== undefined) {
                        scene.logInertia.set(key, inertia);
                    }
                }
            } else if (update.action === 'place' && update.tileType === TileType.SKY) {
                if (isLocalPrediction) {
                    // Combat echo already predicted — skip instability propagation
                    // (client already propagated when it predicted the destruction)
                } else {
                    // Remote combat destroyed a block: propagate instability
                    scene.localFallingBlocks.delete(key);
                    scene.freshFallingBlocks.delete(key);
                    scene.logInertia.delete(key);
                    scene.propagateLocalInstabilityUp(update.x, update.y - 1);
                }
            } else if (update.action !== 'place') {
                if (!fallingBlockLandedHere) {
                    scene.localFallingBlocks.delete(key);
                    scene.freshFallingBlocks.delete(key);
                }
                scene.logInertia.delete(key);
                // Propagate instability: blocks above should start falling
                // Deferred to echo (like placement) to keep falling synced
                scene.propagateLocalInstabilityUp(update.x, update.y - 1);
            }
        }
        scene.pendingServerBlockUpdates = [];

        // Continuous sword tile prediction — mirrors server's tickCombat sword section.
        // Check every tick while holding sword so sticks/ladders that fall into
        // the sword position are destroyed locally without waiting for server echo.
        if (player.isHoldingSword) {
            scene.predictSwordTile(player, globalTick);
        }

        // Tick local falling blocks — fresh blocks from this tick's updates
        // will be promoted to the active set at the END of this call,
        // giving them a 1-tick grace period before they start moving.
        scene.tickLocalFallingBlocks();

        // Process pending walk-off-ledge (diagonal move synced to global tick)
        if (player.state === PlayerState.FALLING && player.pendingLedgeDir !== 0) {
            const dir = player.pendingLedgeDir;
            player.pendingLedgeDir = 0; // Clear pending
            player.facing = dir;

            // Do the diagonal: horizontal + down
            player.x += dir;
            scene.tryMoveDown(player);
            scene.lastHorizontalMoveTime = now;
            scene.lastUnifiedMoveTime = now;

            // Check if landed (unlikely but possible if there was ground 2 below)
            if (scene.isOnGround(player)) {
                player.state = player.isCrouching ? PlayerState.CROUCHING : PlayerState.IDLE;
            }
            // Skip regular falling - we already processed this tick's fall
        }
        // Glider: move forward 1 tile/tick, no fall
        else if (player.isGliding && player.state === PlayerState.FALLING) {
            scene.tryMoveHorizontal(player, player.glideDir);
            scene.lastHorizontalMoveTime = now;
            scene.lastUnifiedMoveTime = now;
            // If we hit something or landed, stop gliding
            if (scene.isOnGround(player)) {
                player.isGliding = false;
                player.state = player.isCrouching ? PlayerState.CROUCHING : PlayerState.IDLE;
            }
        }
        // Regular falling (no pending ledge)
        else if (player.state === PlayerState.FALLING && !scene.isOnGround(player)) {
            scene.tryMoveDown(player);

            // HORIZONTAL AIR-STRAFE synced to fall tick (for perfect diagonal)
            // Skip if just pressed OR if instant move consumed this tick's horizontal
            if (scene.skipNextTickHorizontal) {
                // Instant move already did horizontal - skip this tick, clear flag
                scene.skipNextTickHorizontal = false;
            } else if (!scene.horizontalJustPressed && wantsHorizontal) {
                const dir = scene.inputState.left ? -1 : 1;
                player.facing = dir;
                if (scene.tryMoveHorizontal(player, dir)) {
                    scene.lastHorizontalMoveTime = now;
                    // Send air strafe to other clients for immediate update
                    scene.networkClient.sendAirStrafe(dir);
                }
            }

            // Process pending slash AFTER air strafe movement
            // This ensures slash spawns at player's new position, synced with fall tick
            if (player.pendingSlash) {
                player.pendingSlash = false;
                scene.spawnLocalSlash(player, now);
                player.lastSlashSpawnTick = globalTick;
            }

            // Sync unified tick timer to prevent double-move if we land
            scene.lastUnifiedMoveTime = now;

            // Check if landed
            if (scene.isOnGround(player)) {
                player.state = player.isCrouching ? PlayerState.CROUCHING : PlayerState.IDLE;
            }
        }

        // Process pending block placement/pickup AFTER fall (so position is synced)
        // Use CURRENT position (after both player and blocks have fallen together)
        if (player.pendingBlockPlace || player.pendingBlockPickup) {
            player.pendingBlockPlace = false;
            player.pendingBlockPickup = false;
            executeDownAction(scene, player, false); // Use current position, not stored
        }

        // Process pending spacebar/A-button block action (queued while falling)
        if (player.pendingAttackBlock) {
            player.pendingAttackBlock = false;
            executeAttackBlockAction(scene, player);
        }

        // Also check if player should START falling (ground just disappeared)
        if ((player.state === PlayerState.IDLE || player.state === PlayerState.WALKING ||
             player.state === PlayerState.CROUCHING) && !scene.isOnGround(player)) {
            player.state = PlayerState.FALLING;
            scene.tryMoveDown(player);
        }

        // NOTE: pendingSlash processing removed from here - it was firing before
        // unified tick movement. pendingSlash is now only processed in unified tick
        // section (after horizontal movement) for correct timing.
    }
}

export function processContinuousCombatInput(scene: GameSceneMotionContext, player: any, now: number): void {
    // Force-release sword if player walks into spawn protection zone while holding
    if (player.isHoldingSword && isInSpawnProtection(player.x, player.y, scene.roomBoxes)) {
        player.isHoldingSword = false;
        scene.networkClient.sendSwordHold(false);
        return;
    }

    // Continuous slash spawning while holding sword (skip for club — melee only)
    if (player.isHoldingSword && player.equippedItem !== TileType.ILO_IJO) {
        const ticksHeld = Math.floor(now / MOVE_INTERVAL) - player.swordHoldStartTick;
        // Spawn every SLASH_RATE ticks (skip tick 0, that was first slash)
        if (ticksHeld > 0 && ticksHeld % SLASH_RATE === 0) {
            const globalTick = Math.floor(now / MOVE_INTERVAL);
            if (globalTick > player.lastSlashSpawnTick) {
                // Check if player is walking or standing still
                const playerJustMoved = (now - scene.lastHorizontalMoveTime) < 20;
                const playerIsWalking = (now - scene.lastHorizontalMoveTime) < MOVE_INTERVAL * 1.2;

                if (playerIsWalking) {
                    // Walking: only spawn when player moves (sync)
                    if (playerJustMoved) {
                        scene.spawnLocalSlash(player, now);
                        player.lastSlashSpawnTick = globalTick;
                    }
                } else {
                    // Standing still: spawn on tick timing
                    scene.spawnLocalSlash(player, now);
                    player.lastSlashSpawnTick = globalTick;
                }
            }
        }
    }
}

export function doVerticalMove(scene: GameSceneMotionContext, player: any, now: number): void {
    // Glider (ILO_WASO): when A is held in air with empty head, float forward
    if (player.equippedItem === TileType.ILO_WASO
        && scene.inputState.attack
        && !player.isHoldingBlock
        && (player.state === PlayerState.FALLING || player.state === PlayerState.RISING)) {
        if (!player.isGliding) {
            player.isGliding = true;
            player.glideDir = player.facing;
        }
        // Suppress vertical movement — stay at current Y
        player.state = PlayerState.FALLING; // Keep falling state (but don't actually fall)
        return;
    } else if (player.isGliding) {
        player.isGliding = false;
    }

    // Rising - uses unified tick timing (instant-first)
    if (player.state === PlayerState.RISING) {
        const mustJump = player.minJumpRemaining > 0;
        const wantsJump = scene.inputState.up && player.jumpRemaining > 0;

        if (mustJump || wantsJump) {
            if (scene.tryMoveUp(player)) {
                player.jumpRemaining--;
                if (player.minJumpRemaining > 0) player.minJumpRemaining--;
                // Ladder jump refresh: seamless, only counter changes
                if (scene.isInLadder(player)) {
                    player.jumpRemaining = player.jumpHeight;
                }
            } else {
                player.minJumpRemaining = 0;
                player.state = PlayerState.FALLING;
            }
        } else {
            player.state = PlayerState.FALLING;
        }
        return;
    }

    // Falling movement is now handled by global-aligned tick (above in updateLocalPlayer)
    // Just handle state transitions here
    if (!scene.isOnGround(player) && player.state !== PlayerState.FALLING) {
        player.state = PlayerState.FALLING;
    } else if (scene.isOnGround(player) && player.state === PlayerState.FALLING) {
        player.state = player.isCrouching ? PlayerState.CROUCHING : PlayerState.IDLE;
    }

    // Update crouching state
    if (player.isCrouching && scene.isOnGround(player)) {
        player.state = PlayerState.CROUCHING;
    }
}

export function checkFallOffLedge(scene: GameSceneMotionContext, player: any): void {
    // If we're supposedly grounded but actually have no floor
    if (player.state === PlayerState.IDLE || player.state === PlayerState.WALKING ||
        player.state === PlayerState.CROUCHING) {
        if (!scene.isOnGround(player)) {
            // Try to step down 1 tile
            scene.tryMoveDown(player);

            // Did we land? (1-tile step down)
            if (scene.isOnGround(player)) {
                // Landed - stay in normal state, diagonal step complete
                player.state = player.isCrouching ? PlayerState.CROUCHING : PlayerState.IDLE;
            } else {
                // Still no ground - start falling, wait for global tick
                player.state = PlayerState.FALLING;
            }
        }
    }
}
